/**
 * Pure date/grid helpers behind the week strip and month calendar in
 * `WeekNav.vue`. No data lives here — `timesheet2-store.js` combines these
 * with whatever `getUserTimesheetSummariesForDateRangeAsync()` returns.
 *
 * (Real day navigation; the review banner's content is `timesheet2-review.js`.)
 */

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MONTH_LABELS = ["January", "February", "March", "April", "May", "June",
	"July", "August", "September", "October", "November", "December"];

const SHORT_MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
	"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// The strip shows the whole week, Mon–Sun, so weekend timesheets are reachable
// without going through the calendar.
export const DAYS_IN_WEEK = 7;




/**
 * The Monday of the week a date falls in.
 */
export function startOfWeek(date)
{
	const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const offset = (monday.getDay() + 6) % 7;	// Sunday (0) is 6 days into the week.

	monday.setDate(monday.getDate() - offset);

	return monday;
}




export function addDays(date, days)
{
	const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	result.setDate(result.getDate() + days);

	return result;
}




export function toIsoDate(date)
{
	return date.getFullYear()
		+ "-" + String(date.getMonth() + 1).padStart(2, "0")
		+ "-" + String(date.getDate()).padStart(2, "0");
}




export function parseIsoDate(isoDate)
{
	return new Date(Number(isoDate.substring(0, 4)), Number(isoDate.substring(5, 7)) - 1, Number(isoDate.substring(8, 10)));
}




export function todayIsoDate()
{
	return toIsoDate(new Date());
}




/**
 * ISO dates ("yyyy-mm-dd") sort lexicographically in calendar order, so a
 * plain string comparison against today is enough — no date parsing needed.
 */
export function isFutureIsoDate(isoDate)
{
	return isoDate > todayIsoDate();
}




/**
 * The Mon–Sun dates of the week starting on `weekStartIso`.
 */
export function buildWeekDayDates(weekStartIso)
{
	const weekStart = parseIsoDate(weekStartIso);
	const days = [];

	for (let dayOffset = 0; dayOffset < DAYS_IN_WEEK; dayOffset++)
	{
		const date = addDays(weekStart, dayOffset);

		days.push({
			Date: toIsoDate(date),
			Label: WEEKDAY_LABELS[date.getDay()] + " " + String(date.getDate()).padStart(2, "0"),
		});
	}

	return days;
}




export function formatWeekLabel(weekStartIso)
{
	const weekStart = parseIsoDate(weekStartIso);

	return "Week of " + String(weekStart.getDate()).padStart(2, "0") + " " + SHORT_MONTH_LABELS[weekStart.getMonth()];
}




/**
 * "Mon, 17 Aug" — includes the month so a tooltip stays unambiguous while
 * scrolling across month boundaries, unlike the day pill's own "Mon 17" label.
 */
export function formatDayWithMonth(isoDate)
{
	const date = parseIsoDate(isoDate);

	return WEEKDAY_LABELS[date.getDay()] + ", " + date.getDate() + " " + SHORT_MONTH_LABELS[date.getMonth()];
}




export function formatMonthLabel(year, month)
{
	return MONTH_LABELS[month] + " " + year;
}




/**
 * A 6x7 grid of dates covering `year`/`month` (0-based), including the
 * leading/trailing days of the adjacent months needed to fill whole weeks.
 */
export function buildMonthGridDates(year, month)
{
	const firstOfMonth = new Date(year, month, 1);
	const gridStart = startOfWeek(firstOfMonth);

	const cells = [];

	for (let index = 0; index < 42; index++)
	{
		const date = addDays(gridStart, index);

		cells.push({
			Date: toIsoDate(date),
			DayOfMonth: date.getDate(),
			InMonth: date.getMonth() === month && date.getFullYear() === year,
		});
	}

	return cells;
}




export function monthKey(year, month)
{
	return year + "-" + String(month + 1).padStart(2, "0");
}




/**
 * The month(s) (as `{ Year, Month }`) that need to be loaded to cover a
 * Mon–Sun week — a week straddling a month boundary touches two.
 */
export function monthsTouchedByWeek(weekStartIso)
{
	const weekStart = parseIsoDate(weekStartIso);
	const weekEnd = addDays(weekStart, DAYS_IN_WEEK - 1);

	const months = [{ Year: weekStart.getFullYear(), Month: weekStart.getMonth() }];
	const endKey = monthKey(weekEnd.getFullYear(), weekEnd.getMonth());

	if (monthKey(months[0].Year, months[0].Month) !== endKey) months.push({ Year: weekEnd.getFullYear(), Month: weekEnd.getMonth() });

	return months;
}




export function nextMonth(year, month)
{
	return month === 11 ? { Year: year + 1, Month: 0 } : { Year: year, Month: month + 1 };
}




export function previousMonth(year, month)
{
	return month === 0 ? { Year: year - 1, Month: 11 } : { Year: year, Month: month - 1 };
}




export function currentYearMonth()
{
	const today = new Date();

	return { Year: today.getFullYear(), Month: today.getMonth() };
}
